/**
 * Filters an already-fetched topology to one or more origins.
 *
 * Done in the browser rather than by refetching, because the whole graph is 402 connected
 * nodes and arrives in one request anyway. Asking the server per switch cost a round trip
 * each time — about 300ms locally, and a visible pause on every chip — for work that is a
 * linear pass over data already in memory.
 *
 * The endpoint keeps its own `surface` parameter for callers that want the server to do
 * it; this is the path the surface chips use.
 */

// Not surfaces. The compliance taxonomy, policies and alert types are shared by every
// origin: a Control reached from a Copilot Studio agent is the same Control a Gemini
// Enterprise agent reaches. Filtering must not simply exclude them, or every chain is cut
// at its first hop.
const SHARED_PLATFORMS = new Set(['compliance', 'policy', 'alerts']);

/**
 * @param {{nodes: Array, links: Array}} topology
 * @param {string[]} surfaces — empty means every origin
 */
export function filterBySurface(topology, surfaces = []) {
  const nodes = topology?.nodes || [];
  const links = topology?.links || [];
  if (!surfaces.length || !nodes.length) return topology;

  const wanted = new Set(surfaces);
  const byId = new Map(nodes.map((n) => [n.id, n]));

  // A node belongs to *another* origin when it carries a surface identity that isn't
  // selected. Two cases:
  //   1. an agent whose platform isn't selected — including one with no platform at all,
  //      which otherwise slipped through the old `!n.platform` structural exemption and
  //      showed non-custom agents under the Custom chip;
  //   2. any node carrying a foreign platform that isn't part of the shared taxonomy
  //      (e.g. a Gemini application).
  // We never show these and never walk through them, so selecting Custom stays Custom.
  // A node's origins are a LIST, not a scalar. Ontology keying merges the same entity
  // across surfaces -- one Account for a person seen on both Copilot and Gemini
  // Enterprise -- and the server projects `platform` as head(surfaces), i.e. whichever
  // origin was written first. Testing that scalar silently drops every node that belongs
  // to the selected surface but was first seen elsewhere: on org 3, seven of the eight
  // Gemini Enterprise users vanished because their list happened to start with
  // 'gcp_audit' or 'microsoft_365_copilot'. Membership over the whole list is what the
  // server's own Cypher does, so match it here.
  const originsOf = (n) => {
    if (Array.isArray(n?.surfaces) && n.surfaces.length) return n.surfaces;
    return n?.platform ? [n.platform] : [];
  };
  const onSelected = (n) => originsOf(n).some((s) => wanted.has(s));
  const isShared = (n) => originsOf(n).some((s) => SHARED_PLATFORMS.has(s));

  const isForeign = (n) => {
    if (!n) return true;
    if (n.type === 'agent') return !onSelected(n);
    // Carries an origin, none of them selected, and is not shared taxonomy.
    return !!(originsOf(n).length && !onSelected(n) && !isShared(n));
  };

  // Seeds are only what actually sits on a selected surface (e.g. the Custom agents/apps).
  const seeds = nodes.filter(onSelected);
  if (!seeds.length) return { ...topology, nodes: [], links: [] };

  const adjacency = new Map();
  links.forEach((l) => {
    const s = typeof l.source === 'object' ? l.source.id : l.source;
    const t = typeof l.target === 'object' ? l.target.id : l.target;
    if (!adjacency.has(s)) adjacency.set(s, []);
    if (!adjacency.has(t)) adjacency.set(t, []);
    adjacency.get(s).push(t);
    adjacency.get(t).push(s);
  });

  // Walk out from the seeds, admitting only what they actually reach — their own models,
  // tools, datasources, sub-agents and the shared compliance/policy/alert taxonomy — and
  // stopping at any foreign node. So Custom shows Custom agents plus only the assets and
  // controls they touch, not every structural node nor any other surface's agents.
  const keep = new Set(seeds.map((n) => n.id));
  const frontier = [...keep];
  while (frontier.length) {
    const current = frontier.pop();
    (adjacency.get(current) || []).forEach((nextId) => {
      if (keep.has(nextId)) return;
      if (isForeign(byId.get(nextId))) return;
      keep.add(nextId);
      frontier.push(nextId);
    });
  }

  return {
    ...topology,
    nodes: nodes.filter((n) => keep.has(n.id)),
    links: links.filter((l) => {
      const s = typeof l.source === 'object' ? l.source.id : l.source;
      const t = typeof l.target === 'object' ? l.target.id : l.target;
      return keep.has(s) && keep.has(t);
    }),
  };
}

export default filterBySurface;
